import type { ReactNode } from "react";

import type { CalendarColor, Label, Template, TemplateLibrary } from "@/api";
import { labelHex } from "@/lib/domain";

/**
 * The Template library as Label columns, in the order the server computed
 * (Priority first, then sort order, then title). renderCard decides how a
 * Template looks and behaves in a given column.
 */
export function LibraryBoard({
  library,
  labels,
  colors,
  renderCard,
}: {
  library: TemplateLibrary;
  labels: Label[];
  colors: CalendarColor[];
  renderCard: (
    template: Template,
    label: Label,
    hex: string | undefined,
  ) => ReactNode;
}) {
  const byId = new Map(library.templates.map((t) => [t.id, t]));
  const labelById = new Map(labels.map((l) => [l.id, l]));
  // Columns wrap into rows instead of forcing a horizontal scroll: each takes
  // at least 200px and grows to share the width, so three or four Labels fit
  // side by side on a laptop and stack on a phone.
  return (
    <div className="flex min-w-0 flex-wrap gap-3">
      {library.columns.map((col) => {
        const label = labelById.get(col.label_id);
        if (!label) return null;
        const hex = labelHex(colors, label);
        return (
          <section
            key={col.label_id}
            className="min-w-0 flex-[1_1_200px] rounded-xl border border-border bg-secondary/30"
            aria-label={`${label.name} column`}
          >
            <header className="flex items-center gap-2 border-b border-border px-3 py-2">
              <span
                className="h-3 w-3 rounded-full border border-black/10 bg-muted-foreground"
                style={hex ? { backgroundColor: hex } : undefined}
                aria-hidden
              />
              <span className="truncate text-sm font-semibold">
                {label.name}
              </span>
              <span className="ml-auto text-xs text-muted-foreground">
                {col.template_ids.length}
              </span>
            </header>
            <ul className="space-y-2 p-2">
              {col.template_ids.length === 0 && (
                <li className="px-1 py-2 text-xs text-muted-foreground">
                  Nothing here yet.
                </li>
              )}
              {col.template_ids.map((id) => {
                const t = byId.get(id);
                return t ? <li key={id}>{renderCard(t, label, hex)}</li> : null;
              })}
            </ul>
          </section>
        );
      })}
    </div>
  );
}
